import { useState } from 'react';
import { CheckboxFilter } from '../components/CheckboxFilter';
import { FitBreakdown } from '../components/FitBreakdown';
import { JobCard } from '../components/JobCard';
import { PageHeader } from '../components/PageHeader';
import { ResultSummary } from '../components/ResultSummary';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '../components/StatePanel';
import { TruncationNotice } from '../components/TruncationNotice';
import { useDebounced } from '../hooks/useDebounced';
import { useJobFit, useJobs, useProfiles, useTaxonomy } from '../hooks/useRadarQuery';
import { compactMoney, label } from '../lib/format';
import type { Job, JobFit } from '../lib/types';

// Round floors rather than a free-text box: an arbitrary number invites a
// precision the corpus does not have, and the live bands run 70k to 350k.
const PAY_FLOORS = [100_000, 150_000, 200_000, 250_000];

export function JobsPage() {
  const [q, setQ] = useState('');
  // Empty means the plain listing. Choosing a profile switches the order to a
  // ranking and attaches a verdict sheet to each posting; it narrows nothing,
  // because a criterion the employer never answered is not a reason to hide a
  // role. The filters above still do the narrowing.
  const [profile, setProfile] = useState('');
  const [status, setStatus] = useState('open');
  // Sets rather than single values: "machine learning or research" is one
  // question a reader asks, and asking it used to mean running the page twice
  // and merging by eye. Empty means the filter asks nothing, which is the rule
  // `OpportunityProfile` already states for its own criteria.
  const [role, setRole] = useState<string[]>([]);
  const [seniority, setSeniority] = useState<string[]>([]);
  const [remotePolicy, setRemotePolicy] = useState<string[]>([]);
  const [paysAtLeast, setPaysAtLeast] = useState('');
  // Searching in the browser also searched less than it appeared to: it matched
  // the company name against the field the API sent with each posting, while
  // the query behind it covered only title and location. Both now run server
  // side against the same three fields.
  //
  // Role family and seniority are sent the same way. They are classified from
  // the title on the way in and were queryable by nothing, so a registry built
  // to notice a company hiring its first manufacturing or field-deployment
  // roles could not be asked which companies were doing that.
  //
  // Arrangement and pay join them for the same reason. Remote policy has been
  // derived on every posting since the first release; pay is read from the body
  // where the board publishes no field. Thirteen remote-eligible roles and
  // fifteen paying $200k or more sit inside 172, and finding either by eye is
  // the work this page exists to remove.
  const filters = {
    q: useDebounced(q),
    status,
    function: role,
    seniority,
    remotePolicy,
    paysAtLeast,
  };
  const listing = useJobs(filters);
  const ranked = useJobFit(profile, filters);
  const taxonomy = useTaxonomy();
  const profiles = useProfiles();
  const query = profile ? ranked : listing;

  if (query.isLoading || taxonomy.isLoading) return <LoadingPanel />;
  if (query.isError || taxonomy.isError || !taxonomy.data) {
    return <ErrorPanel error={query.error ?? taxonomy.error} />;
  }
  const matched = query.data?.total ?? 0;
  const items = query.data?.items ?? [];
  return (
    <>
      <PageHeader eyebrow="Career intelligence" title="Opportunity radar" description="Direct ATS observations preserve first-seen, last-seen, changes, and removals instead of treating listings as disposable links." />
      <section className="filter-bar filter-bar--jobs">
        <label className="search-field"><span>Search roles</span><input value={q} onChange={(event) => setQ(event.target.value)} placeholder="Title, company, or location" /></label>
        <CheckboxFilter legend="Role family" emptyLabel="All families" options={taxonomy.data.jobFunctions} selected={role} onChange={setRole} />
        <CheckboxFilter legend="Seniority" emptyLabel="All levels" options={taxonomy.data.jobSeniorities} selected={seniority} onChange={setSeniority} />
        <CheckboxFilter legend="Arrangement" emptyLabel="Any arrangement" options={taxonomy.data.remotePolicies} selected={remotePolicy} onChange={setRemotePolicy} />
        {/* "Stated pay from" and not "pay from": three quarters of postings
            publish no band, and this filter excludes them rather than assuming
            they clear it. Labelling it otherwise would read as a claim about
            every role rather than about the ones that said. */}
        <label><span>Stated pay from</span><select value={paysAtLeast} onChange={(event) => setPaysAtLeast(event.target.value)}><option value="">Any, including unstated</option>{PAY_FLOORS.map((floor) => <option key={floor} value={String(floor)}>{compactMoney(floor)}+</option>)}</select></label>
        {/* Only offered when a profile exists. A selector listing nothing
            reads as a broken control rather than as an unused feature. */}
        {(profiles.data?.items.length ?? 0) > 0 && (
          <label><span>Rank against</span><select value={profile} onChange={(event) => setProfile(event.target.value)}><option value="">No profile, newest first</option>{profiles.data?.items.map((saved) => <option key={saved.id} value={saved.slug}>{saved.name}</option>)}</select></label>
        )}
        <label><span>Status</span><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">All statuses</option><option value="open">Open</option><option value="no_longer_observed">No longer observed</option></select></label>
      </section>
      <ResultSummary count={matched} one="observed role matches." many="observed roles match." />
      <TruncationNotice shown={items.length} total={matched} noun="observed roles" />
      <section className="card-grid">
        {items.length === 0 && <EmptyPanel message="No matching roles." />}
        {profile
          ? (items as JobFit[]).map((fit) => (
              <div key={fit.job.id} className="ranked-role">
                <JobCard job={fit.job} />
                <FitBreakdown fit={fit} />
              </div>
            ))
          : (items as Job[]).map((job) => <JobCard key={job.id} job={job} />)}
      </section>
    </>
  );
}
